import { tokens } from '@neo4j-ndl/base';
import { DbmsIcon, ServerIconOutline } from '@neo4j-ndl/react/icons';
import type { Relationship } from '@neo4j-nvl/base';
import type { Node as NvlNode } from '@neo4j-nvl/base/dist/types/types/graph-element';
import { FleetManagementTypes } from '@nx/state';
import { renderToStaticMarkup } from 'react-dom/server';

import { ExploreIconOutline } from '../../../../upx/src/components/icons';

export const colors = {
  server: tokens.colors.baltic[35],
  systemDatabase: tokens.colors.neutral[35],
  database: tokens.colors.earth[35],
  online: tokens.colors.forest[35],
  offline: tokens.colors.hibiscus[35],
  degraded: tokens.colors.marigold[35],
};

const HtmlElementWithIcon = (elem: JSX.Element) => {
  const divWithIcon = document.createElement('div');
  divWithIcon.style.display = 'flex';
  divWithIcon.style.justifyContent = 'center';
  divWithIcon.style.alignItems = 'flex-end';
  divWithIcon.style.height = '44px';
  divWithIcon.style.width = '48px';
  divWithIcon.innerHTML = renderToStaticMarkup(elem);

  return divWithIcon;
};

export const getDbmsNode = (d: FleetManagementTypes.DbmsDetails): NvlNode => {
  let color;
  switch (d.status) {
    case FleetManagementTypes.Api.DEPLOYMENT_STATUS.DEGRADED:
      color = colors.degraded;
      break;
    case FleetManagementTypes.Api.DEPLOYMENT_STATUS.RUNNING:
      color = colors.online;
      break;
    default:
      color = colors.offline;
  }
  return {
    id: d.dbmsId,
    captionAlign: 'top',
    captions: [{ value: d.name }],
    color: color,
    html: HtmlElementWithIcon(<DbmsIcon className="h-4" />),
  };
};

export const getServerNode = (s: FleetManagementTypes.Server): NvlNode => {
  return {
    id: s.serverId,
    captionAlign: 'top',
    captions: [{ value: s.address }],
    color: colors.server,
    html: HtmlElementWithIcon(<ServerIconOutline className="h-4" />),
  };
};

export const getDatabaseNode = (d: FleetManagementTypes.Database, dbmsId: string): NvlNode => {
  return {
    id: `db${d.databaseId}${d.name}${dbmsId}`,
    captionAlign: 'top',
    captions: [{ value: d.name }],
    color: d.name === 'system' ? colors.systemDatabase : colors.database,
    html: HtmlElementWithIcon(<ExploreIconOutline className="h-4" />),
  };
};

export const getDbmsToServerRelationship = (
  dbms: FleetManagementTypes.DbmsDetails,
  s: FleetManagementTypes.Server,
): Relationship => {
  return {
    id: `${dbms.dbmsId}-${s.serverId}`,
    from: dbms.dbmsId,
    to: s.serverId,
    captions: [{ value: 'COMPRISES' }],
  };
};

export const getDbmsToDatabaseRelationship = (
  dbms: FleetManagementTypes.DbmsDetails,
  d: FleetManagementTypes.Database,
) => {
  return {
    id: `${dbms.dbmsId}-${d.databaseId}${d.name}`,
    from: dbms.dbmsId,
    to: `db${d.databaseId}${d.name}${dbms.dbmsId}`,
    captions: [{ value: 'HAS_DATABASE' }],
  };
};

export const getServerToDatabaseRelationship = (di: FleetManagementTypes.DatabaseInstance, dbmsId: string) => {
  return {
    id: `${di.serverId}-db${di.databaseId}${di.databaseName}${dbmsId}`,
    from: di.serverId,
    to: `db${di.databaseId}${di.databaseName}${dbmsId}`,
    captions: [{ value: `${di.role.toUpperCase()}_FOR` }],
  };
};

export const addServerNode = (
  s: FleetManagementTypes.Server,
  newNodes: NvlNode[],
  newRels: Relationship[],
  dbms: FleetManagementTypes.DbmsDetails,
) => {
  newNodes.push(getServerNode(s));
  newRels.push(getDbmsToServerRelationship(dbms, s));
};

export const addDatabaseNode = (
  newNodes: NvlNode[],
  d: FleetManagementTypes.Database,
  newRels: Relationship[],
  dbms: FleetManagementTypes.DbmsDetails,
) => {
  newNodes.push(getDatabaseNode(d, dbms.dbmsId));
  newRels.push(getDbmsToDatabaseRelationship(dbms, d));
};
